import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { ArrowUpRight, CircleAlert, CircleCheck, RotateCw, Sparkles } from 'lucide-react';
import DOMPurify from 'dompurify';
import { Button, Dialog, DialogFooter, DialogHeader, Field, SegmentedControl, Spinner, Textarea } from '../ui';
import { useAuth } from '../../contexts/AuthContext';
import { intelligenceReportService, ReportDepth, ReportLanguage } from '../../services/intelligenceReportService';
import type { ReportRunStats } from '../../types/news';
import { GenerationSteps, GenerationStatus } from './GenerationSteps';

interface IntelligenceReportGeneratorProps {
  entityType: 'stakeholder' | 'organization';
  entityId: string;
  entityName: string;
  onComplete?: (wikiPageId: string) => void;
}

const STEPS = [
  'Reading the directory record',
  'Connecting to the research provider',
  'Researching and writing',
  'Verifying sources',
  'Saving to the wiki',
  'Complete',
];

const DEPTH_HINT: Record<ReportDepth, string> = {
  standard: 'Every section, about 3,000 words. A few minutes.',
  deep: 'More search passes, verbatim quotes, about 5,000 words. Takes longer and costs more.',
};

/** Words in the streamed HTML so far, for the footer */
const countWords = (html: string) => {
  const text = html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  return text ? text.split(' ').length : 0;
};

export const IntelligenceReportGenerator: React.FC<IntelligenceReportGeneratorProps> = ({
  entityType,
  entityId,
  entityName,
  onComplete,
}) => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [status, setStatus] = useState<GenerationStatus>('idle');
  const [currentStep, setCurrentStep] = useState(0);
  const [stepMessage, setStepMessage] = useState<string | null>(null);
  const [streamedContent, setStreamedContent] = useState('');
  const [wikiPageId, setWikiPageId] = useState<string | null>(null);
  const [reportTitle, setReportTitle] = useState<string | null>(null);
  const [runStats, setRunStats] = useState<ReportRunStats | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [focus, setFocus] = useState('');
  const [language, setLanguage] = useState<ReportLanguage>('en');
  const [depth, setDepth] = useState<ReportDepth>('standard');
  const abortControllerRef = useRef<AbortController | null>(null);
  const contentEndRef = useRef<HTMLDivElement>(null);
  const contentContainerRef = useRef<HTMLDivElement>(null);

  // Follow the text as it streams, unless the reader has scrolled up
  useEffect(() => {
    if (contentEndRef.current && contentContainerRef.current) {
      const container = contentContainerRef.current;
      const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 160;
      if (isNearBottom) {
        container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
      }
    }
  }, [streamedContent]);

  const sanitizedContent = useMemo(() => (streamedContent ? DOMPurify.sanitize(streamedContent) : ''), [streamedContent]);
  const words = useMemo(() => countWords(streamedContent), [streamedContent]);

  const handleGenerate = useCallback(async () => {
    setStatus('generating');
    setCurrentStep(0);
    setStepMessage(null);
    setStreamedContent('');
    setWikiPageId(null);
    setReportTitle(null);
    setRunStats(null);
    setErrorMessage(null);

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const generator = intelligenceReportService.generateReport(entityType, entityId, controller.signal, {
        focus: focus.trim() || undefined,
        language,
        depth,
      });

      for await (const event of generator) {
        if (controller.signal.aborted) break;

        switch (event.type) {
          case 'progress':
            setCurrentStep(event.step);
            setStepMessage(event.message);
            break;
          case 'content':
            setStreamedContent((prev) => prev + event.text);
            break;
          case 'complete':
            setWikiPageId(event.wikiPageId);
            setReportTitle(event.title);
            setRunStats(event.stats ?? null);
            setCurrentStep(STEPS.length);
            setStatus('complete');
            break;
          case 'error':
            setErrorMessage(event.message || 'An unknown error occurred');
            setStatus('error');
            break;
        }
      }
    } catch (error: any) {
      if (error.name === 'AbortError') {
        setStatus('idle');
        setCurrentStep(0);
        setStreamedContent('');
      } else {
        setErrorMessage(error.message || 'Failed to generate report');
        setStatus('error');
      }
    } finally {
      abortControllerRef.current = null;
    }
  }, [entityType, entityId, focus, language, depth]);

  const handleCancel = useCallback(() => {
    abortControllerRef.current?.abort();
    setStatus('idle');
    setCurrentStep(0);
    setStepMessage(null);
    setStreamedContent('');
    setErrorMessage(null);
  }, []);

  const handleClose = useCallback(() => {
    if (status === 'generating') handleCancel();
    setIsOpen(false);
    // Reset after the close animation
    setTimeout(() => {
      setStatus('idle');
      setCurrentStep(0);
      setStepMessage(null);
      setStreamedContent('');
      setWikiPageId(null);
      setReportTitle(null);
      setRunStats(null);
      setErrorMessage(null);
      setFocus('');
    }, 300);
  }, [status, handleCancel]);

  const handleViewReport = useCallback(() => {
    if (wikiPageId && onComplete) onComplete(wikiPageId);
    handleClose();
  }, [wikiPageId, onComplete, handleClose]);

  // Only admins can generate reports — checked after all hooks
  if (!user || (user.role !== 'admin' && user.role !== 'super_admin')) {
    return null;
  }

  const summaryLine = runStats
    ? [
        `${runStats.sections} sections`,
        `${runStats.words.toLocaleString()} words`,
        runStats.sources.cited
          ? `${runStats.sources.cited} sources, ${runStats.sources.reachable} reachable${
              runStats.sources.unreachable ? `, ${runStats.sources.unreachable} unreachable` : ''
            }`
          : 'no sources cited',
      ].join(' · ')
    : null;

  return (
    <>
      <Button size="sm" variant="secondary" onClick={() => setIsOpen(true)}>
        <Sparkles className="h-4 w-4 text-neutral-500" strokeWidth={1.75} />
        <span className="hidden sm:inline">Intelligence report</span>
        <span className="sm:hidden">Intel report</span>
      </Button>

      <Dialog
        open={isOpen}
        onClose={handleClose}
        labelledBy="intel-report-title"
        handle
        className={`max-h-[92vh] sm:max-h-[86vh] sm:max-w-2xl ${streamedContent ? 'h-[92vh] sm:h-[86vh]' : ''}`}
      >
        <DialogHeader
          id="intel-report-title"
          eyebrow={<span className="block max-w-[260px] truncate sm:max-w-[480px]">{entityName}</span>}
          title="Intelligence report"
          onClose={handleClose}
        />

        {/* ── Options ─────────────────────────────────────────────────── */}
        {status === 'idle' && (
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 pb-5 pt-4">
            <Field
              label="Focus"
              htmlFor="additional-context"
              hint="Optional. Questions to cover in depth; every section is still written."
            >
              <Textarea
                id="additional-context"
                rows={3}
                value={focus}
                onChange={(e) => setFocus(e.target.value)}
                className="resize-none"
                placeholder="e.g. Renewable energy policy, connections in Southeast Asia"
              />
            </Field>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Language" htmlFor="report-language">
                <SegmentedControl<ReportLanguage>
                  label="Language"
                  value={language}
                  onChange={setLanguage}
                  items={[
                    { value: 'en', label: 'English' },
                    { value: 'fr', label: 'Français' },
                  ]}
                />
              </Field>
              <Field label="Depth" htmlFor="report-depth" hint={DEPTH_HINT[depth]}>
                <SegmentedControl<ReportDepth>
                  label="Depth"
                  value={depth}
                  onChange={setDepth}
                  items={[
                    { value: 'standard', label: 'Standard' },
                    { value: 'deep', label: 'Deep' },
                  ]}
                />
              </Field>
            </div>
            <p className="text-[12px] leading-relaxed text-neutral-400">
              The dossier is grounded in what the directory records about {entityName}: ties, roles, influence scores,
              positions on issues, monitoring signals and existing pages. The model researches the open web, reconciles
              the two, and every cited link is checked before the page is saved.
            </p>
          </div>
        )}

        {/* ── Running / done / failed ─────────────────────────────────── */}
        {status !== 'idle' && (
          <>
            <GenerationSteps labels={STEPS} currentStep={currentStep} status={status} message={stepMessage} />

            {status === 'error' && errorMessage && (
              <p
                role="alert"
                className="mx-5 mb-3 flex flex-shrink-0 items-start gap-2 rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700"
              >
                <CircleAlert className="mt-px h-4 w-4 flex-shrink-0" strokeWidth={1.75} />
                <span className="min-w-0 break-words">{errorMessage}</span>
              </p>
            )}

            {status === 'complete' && (
              <div className="mx-5 mb-3 flex-shrink-0 text-[13px]">
                <p className="flex items-center gap-2 text-neutral-500">
                  <CircleCheck className="h-4 w-4 flex-shrink-0 text-emerald-600" strokeWidth={1.75} />
                  <span className="min-w-0 truncate">
                    Saved{reportTitle ? <> as <span className="text-ink">{reportTitle}</span></> : ' to the wiki'}
                  </span>
                </p>
                {summaryLine && <p className="mt-1 pl-6 text-[12px] text-neutral-400">{summaryLine}</p>}
              </div>
            )}

            {/* The report as it is written */}
            {streamedContent && (
              <div
                ref={contentContainerRef}
                className="min-h-0 flex-1 overflow-y-auto overscroll-contain border-t border-neutral-200/70"
              >
                <article
                  className="wiki-article mx-auto max-w-[680px] px-5 py-5"
                  dangerouslySetInnerHTML={{ __html: sanitizedContent }}
                />
                <div ref={contentEndRef} />
              </div>
            )}
          </>
        )}

        <DialogFooter>
          {status === 'idle' && (
            <>
              <Button variant="secondary" onClick={handleClose}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleGenerate}>
                Generate
              </Button>
            </>
          )}

          {status === 'generating' && (
            <>
              <span className="mr-auto inline-flex items-center gap-2 text-[12px] tabular-nums text-neutral-400">
                <Spinner className="h-3 w-3" />
                {words ? `${words.toLocaleString()} words` : 'Researching'}
              </span>
              <Button variant="secondary" onClick={handleCancel}>
                Stop
              </Button>
            </>
          )}

          {status === 'complete' && (
            <>
              <Button variant="secondary" onClick={handleClose}>
                Close
              </Button>
              <Button variant="primary" onClick={handleViewReport}>
                Open
                <ArrowUpRight className="h-4 w-4" strokeWidth={1.75} />
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <Button variant="secondary" onClick={handleClose}>
                Close
              </Button>
              <Button variant="primary" onClick={handleGenerate}>
                <RotateCw className="h-4 w-4" strokeWidth={1.75} />
                Retry
              </Button>
            </>
          )}
        </DialogFooter>
      </Dialog>
    </>
  );
};
